import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { FileSpreadsheet, FileText, Upload, Download, CheckCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ImportSessionsDialog — encapsulates Excel/CSV and PDF (AI-powered) imports for training sessions.
 *
 * Props:
 *   - open: bool
 *   - onClose: () => void
 *   - onComplete: () => void  (called after successful import to trigger parent refresh)
 *   - translate: (key, fallback) => string
 */
const ImportSessionsDialog = ({ open, onClose, onComplete, translate }) => {
  const [importMode, setImportMode] = useState('excel');
  const [exerciseImportFile, setExerciseImportFile] = useState(null);
  const [exerciseImportResults, setExerciseImportResults] = useState(null);
  const [exerciseImporting, setExerciseImporting] = useState(false);
  const [pdfImportFile, setPdfImportFile] = useState(null);
  const [pdfImporting, setPdfImporting] = useState(false);
  const [pdfImportResults, setPdfImportResults] = useState(null);
  const exerciseImportRef = useRef(null);
  const pdfImportRef = useRef(null);

  const reset = () => {
    setExerciseImportFile(null);
    setExerciseImportResults(null);
    setPdfImportFile(null);
    setPdfImporting(false);
    setPdfImportResults(null);
    setImportMode('excel');
    if (exerciseImportRef.current) exerciseImportRef.current.value = '';
    if (pdfImportRef.current) pdfImportRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadExerciseTemplate = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/exercises/template/excel`);
      const { headers, example_row } = response.data;
      const csvContent = [headers.join(','), Object.values(example_row).join(',')].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'exercise_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error downloading template');
    }
  };

  const handleExerciseImport = async () => {
    if (!exerciseImportFile) return;
    setExerciseImporting(true);
    setExerciseImportResults(null);
    try {
      const formData = new FormData();
      formData.append('file', exerciseImportFile);
      const response = await axios.post(`${API_BASE_URL}/api/exercises/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setExerciseImportResults(response.data);
      if (response.data?.total_created > 0 && onComplete) onComplete();
    } catch (error) {
      console.error('Error importing exercises:', error);
      setExerciseImportResults({
        total_processed: 0,
        total_created: 0,
        total_errors: 1,
        errors: [{ row: 0, error: error.response?.data?.detail || 'Unknown error' }],
      });
    } finally {
      setExerciseImporting(false);
    }
  };

  const handlePdfImport = async () => {
    if (!pdfImportFile) return;
    setPdfImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', pdfImportFile);
      const res = await axios.post(`${API_BASE_URL}/api/training-sessions/import-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setPdfImportResults(res.data);
      if (onComplete) onComplete();
    } catch (err) {
      setPdfImportResults({
        error: true,
        message: err.response?.data?.detail || err.message || 'Import failed',
      });
    } finally {
      setPdfImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="bg-gradient-to-br from-green-900 to-teal-900 border-green-700 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-green-200 flex items-center">
            <FileSpreadsheet className="mr-2" size={20} />
            {translate('importSessions', 'Import Sessions')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode tabs */}
          <div className="flex rounded-lg overflow-hidden border border-green-600/50">
            <button
              onClick={() => setImportMode('excel')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                importMode === 'excel' ? 'bg-green-600 text-white' : 'bg-green-900/30 text-green-300 hover:bg-green-800/50'
              }`}
              data-testid="import-mode-excel"
            >
              <FileSpreadsheet size={16} />
              Excel / CSV
            </button>
            <button
              onClick={() => setImportMode('pdf')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                importMode === 'pdf' ? 'bg-teal-600 text-white' : 'bg-green-900/30 text-green-300 hover:bg-green-800/50'
              }`}
              data-testid="import-mode-pdf"
            >
              <FileText size={16} />
              PDF (IA)
            </button>
          </div>

          {importMode === 'excel' ? (
            <>
              <div className="p-4 bg-green-800/30 rounded-lg border border-green-600/30">
                <p className="text-green-300 text-sm mb-3">
                  {translate('downloadTemplateFirst', 'Download the template file, fill it with your exercises, then upload it here.')}
                </p>
                <Button onClick={downloadExerciseTemplate} variant="outline" className="border-green-500 text-green-200 hover:bg-green-700/50">
                  <Download className="mr-2" size={16} />
                  {translate('downloadTemplate', 'Download Template')}
                </Button>
              </div>

              <div className="space-y-2">
                <input
                  type="file"
                  ref={exerciseImportRef}
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setExerciseImportFile(e.target.files[0])}
                  className="hidden"
                />
                <div
                  onClick={() => exerciseImportRef.current?.click()}
                  className="border-2 border-dashed border-green-600 rounded-lg p-6 text-center cursor-pointer hover:border-green-500 hover:bg-green-800/30 transition-colors"
                >
                  {exerciseImportFile ? (
                    <div className="flex items-center justify-center gap-2 text-green-300">
                      <CheckCircle size={20} />
                      <span>{exerciseImportFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-green-300">
                      <Upload size={32} className="mx-auto mb-2 opacity-50" />
                      <p>{translate('selectExcelFile', 'Select Excel or CSV file')}</p>
                      <p className="text-xs text-green-400 mt-1">.xlsx, .xls, .csv</p>
                    </div>
                  )}
                </div>
              </div>

              {exerciseImportResults && (
                <div className="space-y-3 p-4 bg-green-800/30 rounded-lg border border-green-700/50">
                  <h4 className="font-semibold text-green-200">{translate('importResults', 'Import Results')}</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-2 bg-green-700/30 rounded">
                      <div className="text-2xl font-bold text-green-200">{exerciseImportResults.total_processed}</div>
                      <div className="text-xs text-green-400">{translate('processed', 'Processed')}</div>
                    </div>
                    <div className="p-2 bg-teal-700/30 rounded">
                      <div className="text-2xl font-bold text-teal-300">{exerciseImportResults.total_created}</div>
                      <div className="text-xs text-teal-400">{translate('created', 'Created')}</div>
                    </div>
                    <div className="p-2 bg-red-700/30 rounded">
                      <div className="text-2xl font-bold text-red-300">{exerciseImportResults.total_errors}</div>
                      <div className="text-xs text-red-400">{translate('errors', 'Errors')}</div>
                    </div>
                  </div>
                  {exerciseImportResults.errors?.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-32 overflow-y-auto">
                      {exerciseImportResults.errors.map((error, idx) => (
                        <div key={idx} className="text-xs p-2 bg-red-900/30 rounded border border-red-700/50">
                          <span className="text-red-300">{translate('row', 'Row')} {error.row}:</span>
                          <span className="text-red-200 ml-2">{error.error}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="p-4 bg-teal-800/30 rounded-lg border border-teal-600/30">
                <p className="text-teal-300 text-sm">
                  {translate('pdfImportDescription', 'Upload a PDF training plan. The AI will analyze the document and automatically create the training session with all exercises.')}
                </p>
              </div>

              <div className="space-y-2">
                <input
                  type="file"
                  ref={pdfImportRef}
                  accept=".pdf"
                  onChange={(e) => setPdfImportFile(e.target.files[0])}
                  className="hidden"
                />
                <div
                  onClick={() => pdfImportRef.current?.click()}
                  className="border-2 border-dashed border-teal-600 rounded-lg p-6 text-center cursor-pointer hover:border-teal-500 hover:bg-teal-800/30 transition-colors"
                  data-testid="pdf-upload-zone"
                >
                  {pdfImportFile ? (
                    <div className="flex items-center justify-center gap-2 text-teal-300">
                      <CheckCircle size={20} />
                      <span>{pdfImportFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-teal-300">
                      <FileText size={32} className="mx-auto mb-2 opacity-50" />
                      <p>{translate('selectPdfFile', 'Select a PDF training plan')}</p>
                      <p className="text-xs text-teal-400 mt-1">.pdf</p>
                    </div>
                  )}
                </div>
              </div>

              {pdfImporting && (
                <div className="p-4 bg-teal-800/30 rounded-lg border border-teal-600/30 text-center">
                  <div className="animate-pulse text-teal-300 text-sm">
                    {translate('aiAnalyzing', 'AI is analyzing the document...')}
                  </div>
                  <div className="mt-2 h-1.5 bg-teal-900 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-400 rounded-full animate-[loading_2s_ease-in-out_infinite]" style={{ width: '70%' }}></div>
                  </div>
                </div>
              )}

              {pdfImportResults && !pdfImportResults.error && (
                <div className="space-y-3 p-4 bg-teal-800/30 rounded-lg border border-teal-700/50">
                  <h4 className="font-semibold text-teal-200">{translate('importSuccess', 'Import Successful')}</h4>
                  <p className="text-teal-300 text-sm font-medium">{pdfImportResults.session_name}</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-2 bg-teal-700/30 rounded">
                      <div className="text-2xl font-bold text-teal-200">{pdfImportResults.total_blocks}</div>
                      <div className="text-xs text-teal-400">Blocs</div>
                    </div>
                    <div className="p-2 bg-green-700/30 rounded">
                      <div className="text-2xl font-bold text-green-300">{pdfImportResults.total_exercises}</div>
                      <div className="text-xs text-green-400">Exercices</div>
                    </div>
                    <div className="p-2 bg-blue-700/30 rounded">
                      <div className="text-2xl font-bold text-blue-300">{pdfImportResults.total_distance_meters}m</div>
                      <div className="text-xs text-blue-400">Distance</div>
                    </div>
                  </div>
                  {pdfImportResults.coach && <p className="text-teal-400 text-xs">Coach: {pdfImportResults.coach}</p>}
                </div>
              )}

              {pdfImportResults && pdfImportResults.error && (
                <div className="p-4 bg-red-900/30 rounded-lg border border-red-700/50">
                  <p className="text-red-300 text-sm">{pdfImportResults.message}</p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClose} className="border-green-500 text-green-300 hover:bg-green-700/50">
            {translate('close', 'Close')}
          </Button>
          {importMode === 'excel' && !exerciseImportResults && (
            <Button
              onClick={handleExerciseImport}
              disabled={!exerciseImportFile || exerciseImporting}
              className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
            >
              {exerciseImporting ? <span className="animate-spin mr-2">...</span> : <Upload className="mr-2" size={16} />}
              {translate('import', 'Import')}
            </Button>
          )}
          {importMode === 'pdf' && !pdfImportResults && (
            <Button
              onClick={handlePdfImport}
              disabled={!pdfImportFile || pdfImporting}
              className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
              data-testid="pdf-import-submit"
            >
              {pdfImporting ? <span className="animate-pulse mr-2">...</span> : <FileText className="mr-2" size={16} />}
              {translate('analyzePdf', 'Analyze PDF')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportSessionsDialog;
